import { ICommonSettings, IIyzicoSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigIyzicoSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingIyzicoSettings?: IIyzicoSetting;
}

export interface IConfigIyzicoSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingIyzicoId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigIyzicoSettings extends React.PureComponent<IConfigIyzicoSettingsProps, IConfigIyzicoSettingsState> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigIyzicoSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadIyzicoSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigIyzicoSettingsProps) {
    if (prevProps.editingIyzicoId && !this.props.editingIyzicoId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingIyzicoId !== this.props.editingIyzicoId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadIyzicoSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadIyzicoSetting();
    }
  }

  loadIyzicoSetting = () => {
    if (!this.props.editingIyzicoId) return;

    const editingIyzicoSettings = this.props.commonSettings.iyzicoSettings?.find(
      (x) => x.id === this.props.editingIyzicoId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (!editingIyzicoSettings) return;
    this.setState({ editingIyzicoSettings }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingIyzicoSettings);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const iyzicoSetting = this.props.commonSettings?.iyzicoSettings;
    if (!iyzicoSetting) return;

    const isSettingAlreadyExist = iyzicoSetting.some(
      (x) => x.name === value && x.id !== this.props.editingIyzicoId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (isSettingAlreadyExist) throw new Error("Iyzico Setting name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IIyzicoSetting;
    if (this.props.editingIyzicoId) {
      obj = {
        id: this.props.editingIyzicoId,
        name: data.name,
        apiKey: data.apiKey?.trim(),
        secretKey: data.secretKey?.trim(),
        baseUrl: data.baseUrl?.trim(),
        title: data.title?.trim()
      };
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "iyzicoSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      obj = {
        id: Guid.create().toString(),
        name: data.name,
        apiKey: data.apiKey?.trim(),
        secretKey: data.secretKey?.trim(),
        baseUrl: data.baseUrl?.trim(),
        title: data.title?.trim()
      };
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "iyzicoSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteIyzico = () => {
    if (!this.props.editingIyzicoId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingIyzicoId || "",
      configKey: "iyzicoSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Iyzico setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Iyzico setting"
        secondHeader="Iyzico setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingIyzicoId ? this.deleteIyzico : undefined}
            deleteTestId="iyzicoSettingDeleteButton"
            submitTestId="iyzicoSettingCreateButton"
            submitLabel={this.props.editingIyzicoId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configIyzicoForm"
          />
        }
      >
        <Form
          id="configIyzicoForm"
          name="iyzicoSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("iyzicosettingpopup_iyzicosetting_description")}
          >
            <div>
              <ConfigField label="Name" required tooltipContentKey="iyzicosettingpopup_iyzicosetting_name">
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: "Please enter a Iyzico Name." },
                    { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Iyzico Setting Name is invalid" },
                    { validator: this.existingNameValidator }
                  ]}
                >
                  <KuikaInput size="md" data-testid="iyzicoSettingNameInput" autoFocus />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Title" tooltipContentKey="iyzicosettingpopup_iyzicosetting_title">
                <Form.Item name="title">
                  <KuikaInput size="md" />
                </Form.Item>
              </ConfigField>
              <ConfigField label="API Key" required tooltipContentKey="iyzicosettingpopup_iyzicosetting_apikey">
                <Form.Item rules={[{ required: true, message: "Iyzico Setting API Key is required" }]} name="apiKey">
                  <KuikaInput size="md" data-testid="iyzicoSettingApiKeyInput" />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Secret Key" required tooltipContentKey="iyzicosettingpopup_iyzicosetting_secretkey">
                <Form.Item
                  rules={[{ required: true, message: "Iyzico Settings Secret Key is required" }]}
                  name="secretKey"
                >
                  <KuikaInput size="md" data-testid="iyzicoSettingSecretKeyInput" />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Base URL" required tooltipContentKey="iyzicosettingpopup_iyzicosetting_baseurl">
                <Form.Item
                  rules={[{ required: true, message: "Iyzico Settings Base URL is required." }]}
                  name="baseUrl"
                >
                  <KuikaInput size="md" data-testid="iyzicoSettingBaseUrlInput" />
                </Form.Item>
              </ConfigField>
            </div>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
